import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, FileText, PenSquare, Sparkles, Heart, Info,
  ThumbsUp, MessageCircle, Eye, Loader2, User
} from "lucide-react";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import MyMembershipCard from "../components/MyMembershipCard";
import { WritePostPage } from "./WritePostPage";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "방금 전";
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
    return d.toLocaleDateString("ko-KR");
  } catch { return ""; }
};

const getAge = (birthYear) => birthYear ? new Date().getFullYear() - birthYear : null;

const InfoTab = () => (
  <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8" data-testid="info-tab-content">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
        <Sparkles className="w-6 h-6 text-amber-500" />
      </div>
      <h2 className="text-xl font-bold text-stone-800">AI 매칭 기능 안내</h2>
    </div>
    <div className="space-y-4 text-sm leading-relaxed text-stone-700">
      <p>
        <span className="font-semibold text-burgundy-600">"글로 만나는 나의 인연 찾기"</span>는
        당신의 글에서 드러나는 가치관·취향·정서를 AI가 분석하여
        <span className="font-semibold"> 결이 맞는 이성을 추천</span>해드리는 기능입니다.
      </p>
      <div className="bg-stone-50 rounded-xl p-4 space-y-3">
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-burgundy-500 text-white text-xs font-bold flex items-center justify-center">1</span>
          <p className="text-stone-700"><b>글을 씁니다.</b> 일상, 연애, 취미, 인생관 등 무엇이든 좋아요. 진솔할수록 매칭이 정확해집니다.</p>
        </div>
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-burgundy-500 text-white text-xs font-bold flex items-center justify-center">2</span>
          <p className="text-stone-700"><b>AI가 분석합니다.</b> GPT 기반 모델이 글의 어조와 관심사를 파악합니다.</p>
        </div>
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-burgundy-500 text-white text-xs font-bold flex items-center justify-center">3</span>
          <p className="text-stone-700"><b>이성을 추천받습니다.</b> 당신과 ±10세 이내의 결이 맞는 이성 1~3명이 즉시 추천됩니다.</p>
        </div>
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-burgundy-500 text-white text-xs font-bold flex items-center justify-center">4</span>
          <p className="text-stone-700"><b>대화를 시작합니다.</b> 추천 결과 화면에서 바로 메세지를 보낼 수 있습니다.</p>
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
        <Info className="w-4 h-4 inline-block mr-1.5 mb-0.5" />
        진솔한 글일수록 매칭이 정교해집니다. 좌측 <b>"글쓰기"</b> 탭에서 바로 작성해보세요.
      </div>
    </div>
  </div>
);

const MyPostsTab = ({ token }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/api/posts/mine/list?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("내 글을 불러오지 못했습니다.");
        const data = await res.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchPosts();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center">
        <Loader2 className="w-7 h-7 text-burgundy-500 animate-spin mx-auto mb-2" />
        <p className="text-sm text-stone-500">불러오는 중...</p>
      </div>
    );
  }
  if (error) {
    return <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-600">{error}</div>;
  }
  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center" data-testid="mine-empty">
        <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
        <p className="text-stone-600 font-medium mb-1">아직 작성한 글이 없습니다</p>
        <p className="text-stone-400 text-sm">좌측 <b>글쓰기</b> 탭에서 첫 글을 남겨보세요.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="mine-list">
      <p className="text-stone-500 text-sm px-1">총 <b className="text-stone-800">{total}</b>개의 글</p>
      {posts.map((p) => (
        <button
          key={p.id}
          onClick={() => navigate(`/community/post/${p.id}`)}
          className="w-full text-left bg-white rounded-xl border border-stone-200 hover:border-burgundy-300 p-4 transition-colors"
          data-testid={`mine-post-${p.id}`}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{p.category_title || p.category}</span>
            <span className="text-[11px] text-stone-400">{formatDate(p.created_at)}</span>
          </div>
          <h3 className="font-semibold text-stone-800 mb-1 line-clamp-1">{p.title}</h3>
          <p className="text-sm text-stone-500 line-clamp-2 mb-2">{p.content}</p>
          <div className="flex items-center gap-3 text-xs text-stone-400">
            <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{p.likes_count || 0}</span>
            <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" />{p.comments_count || 0}</span>
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{p.views || 0}</span>
          </div>
        </button>
      ))}
    </div>
  );
};

const MyMatchesTab = ({ token }) => {
  const navigate = useNavigate();
  const [aiMatches, setAiMatches] = useState([]);
  const [likers, setLikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiMessage, setAiMessage] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [aiRes, likersRes] = await Promise.all([
          fetch(`${API_URL}/api/posts/mine/ai-matches`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/posts/mine/likers`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const aiData = await aiRes.json();
        const likersData = await likersRes.json();
        if (aiRes.ok) {
          setAiMatches(aiData.matches || []);
          setAiMessage(aiData.message || "");
        }
        if (likersRes.ok) setLikers(likersData.likers || []);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchAll();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center">
        <Loader2 className="w-7 h-7 text-burgundy-500 animate-spin mx-auto mb-2" />
        <p className="text-sm text-stone-500">인연을 찾는 중...</p>
      </div>
    );
  }

  const renderUserCard = (m, idx, kind) => (
    <button
      key={`${kind}-${m.user_id || m.id || idx}`}
      onClick={() => navigate(`/community/author/${m.user_id || m.id}`)}
      className="text-left bg-white rounded-xl border border-stone-200 hover:border-burgundy-300 hover:shadow-md transition-all p-4 group"
      data-testid={`match-card-${kind}-${idx}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
          m.gender === "female" ? "bg-rose-100 text-rose-500" : "bg-blue-100 text-blue-500"
        }`}>
          {m.profile_image ? (
            <img src={m.profile_image} alt={m.username} className="w-full h-full object-cover" />
          ) : (
            <User className="w-6 h-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span className="font-semibold text-stone-800 truncate">{m.username || m.nickname || "회원"}</span>
            {m.membership_type && <MembershipBadge type={m.membership_type} size="xs" />}
            {m.document_verified && <DocumentVerifiedBadge size="xs" />}
          </div>
          <p className="text-xs text-stone-500 mb-1">
            {getAge(m.birth_year) ? `${getAge(m.birth_year)}세` : ""} {m.city ? ` · ${m.city}` : ""}
          </p>
          {m.reason && <p className="text-xs text-stone-600 line-clamp-2">{m.reason}</p>}
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="matches-content">
      <section>
        <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          AI가 추천한 인연
          <span className="text-xs font-normal text-stone-400">({aiMatches.length})</span>
        </h3>
        {aiMatches.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {aiMatches.map((m, i) => renderUserCard(m, i, "ai"))}
          </div>
        ) : (
          <div className="bg-stone-50 rounded-xl p-5 text-sm text-stone-500 text-center">
            {aiMessage || "글을 더 작성하시면 결이 맞는 이성이 추천됩니다."}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
          <Heart className="w-4 h-4 text-rose-500" />
          내 글을 좋아해준 회원
          <span className="text-xs font-normal text-stone-400">({likers.length})</span>
        </h3>
        {likers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {likers.map((m, i) => renderUserCard(m, i, "liker"))}
          </div>
        ) : (
          <div className="bg-stone-50 rounded-xl p-5 text-sm text-stone-500 text-center">
            아직 좋아요를 눌러준 이성친구가 없습니다.
          </div>
        )}
      </section>
    </div>
  );
};

export default function MyCornerPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("write"); // info | mine | write | matches

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate("/", { replace: true });
  }, [authLoading, isAuthenticated, navigate]);

  const tabs = [
    { id: "info", label: "AI 매칭 설명", icon: Info },
    { id: "mine", label: "내가 쓴 글", icon: FileText },
    { id: "write", label: "글쓰기", icon: PenSquare },
    { id: "matches", label: "나의 인연", icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-stone-100 pt-20 pb-12" data-testid="my-corner-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate("/community")}
            className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
            data-testid="my-corner-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">글로 만나는 나의 인연</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
          {/* Left Sidebar */}
          <aside className="space-y-4 h-fit lg:sticky lg:top-24" data-testid="my-corner-sidebar">
            {/* 멤버십 상태 카드 */}
            <MyMembershipCard />

            <div className="bg-white rounded-2xl border border-stone-200 p-2">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActive ? "bg-burgundy-50 text-burgundy-600" : "text-stone-600 hover:bg-stone-50"
                      }`}
                      data-testid={`my-corner-tab-${t.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main */}
          <main className="min-w-0" data-testid="my-corner-main">
            {activeTab === "info" && <InfoTab />}
            {activeTab === "mine" && <MyPostsTab token={token} />}
            {activeTab === "write" && (
              <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <WritePostPage embedded />
              </div>
            )}
            {activeTab === "matches" && <MyMatchesTab token={token} />}
          </main>
        </div>
      </div>
    </div>
  );
}
